import React, { useState } from "react";
import {
  ShieldCheck,
  Activity,
  Smartphone,
  Wand2,
  CalendarClock,
  FileLock,
} from "lucide-react";
import { TID } from "@/constants/coverifyIds";

const FEATURES = [
  {
    key: "compliance",
    icon: ShieldCheck,
    title: "Centralised Compliance Control",
    desc:
      "View the verification status of every client in one organised dashboard. Instantly see who's verified, who needs follow-up, and who has failed.",
    metrics: [
      { k: "1,248", l: "Verified" },
      { k: "37", l: "Pending" },
      { k: "4", l: "Attention" },
    ],
  },
  {
    key: "tracking",
    icon: Activity,
    title: "Real-Time Verification Tracking",
    desc: "Track every verification in real time. Pending, Passed or Rejected. Eliminate spreadsheets and status-chase emails forever.",
    metrics: [
      { k: "Live", l: "Updates" },
      { k: "0", l: "Spreadsheets" },
      { k: "<1s", l: "Status sync" },
    ],
  },
  {
    key: "experience",
    icon: Smartphone,
    title: "Professional Client Experience",
    desc:
      "Clients receive clear instructions and complete mobile-optimised identity checks in under five minutes, a smooth, on-brand journey.",
    metrics: [
      { k: "<5m", l: "Avg. time" },
      { k: "98%", l: "First-pass" },
      { k: "iOS · Android · Web", l: "Coverage" },
    ],
  },
  {
    key: "automation",
    icon: Wand2,
    title: "Time-Saving Automation",
    desc:
      "Remove manual tracking, email coordination and status chasing. Automated workflows manage verifications so your team can focus on clients.",
    metrics: [
      { k: "12h", l: "Saved / wk" },
      { k: "Auto", l: "Reminders" },
      { k: "Zero", l: "Manual chases" },
    ],
  },
  {
    key: "deadlines",
    icon: CalendarClock,
    title: "Deadline & Compliance Monitoring",
    desc:
      "Stay ahead of upcoming verification requirements. CoVerify connects with CoSec App to ensure ID is completed before appointment deadlines.",
    metrics: [
      { k: "Smart", l: "Reminders" },
      { k: "ECCTA", l: "Aligned" },
      { k: "100%", l: "On-time" },
    ],
  },
  {
    key: "audit",
    icon: FileLock,
    title: "Complete Audit Trails",
    desc:
      "Every verification attempt is recorded automatically, with a full compliance audit trail to support regulatory and recordkeeping requirements.",
    metrics: [
      { k: "Signed", l: "Reports" },
      { k: "7y", l: "Retention" },
      { k: "Encrypted", l: "Storage" },
    ],
  },
];

export default function KeyFeatures() {
  const [active, setActive] = useState(FEATURES[0].key);
  const current = FEATURES.find((f) => f.key === active);

  return (
    <section id="features" className="py-20 md:py-28">
      <div className="container-cv">
        <div className="max-w-3xl">
          <span className="section-eyebrow">Key features</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.1]">
            Everything you need for{" "}
            <span className="text-accent">inspection-ready</span> verification.
          </h2>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Tab list */}
          <div className="lg:col-span-5">
            <div className="flex flex-col gap-2">
              {FEATURES.map((f) => {
                const isActive = active === f.key;
                const Icon = f.icon;
                return (
                  <button
                    key={f.key}
                    data-testid={TID.featureTab(f.key)}
                    onClick={() => setActive(f.key)}
                    className={`group flex items-center gap-4 rounded-xl border px-4 py-4 text-left transition-all ${
                      isActive
                        ? "border-accent bg-white shadow-[0_8px_24px_-12px_rgba(124,194,66,0.45)]"
                        : "border-border bg-white/60 hover:border-accent/60 hover:bg-white"
                    }`}
                  >
                    <div
                      className={`grid h-10 w-10 place-items-center rounded-lg transition-colors ${
                        isActive ? "bg-accent text-primary" : "bg-secondary text-primary"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-display font-bold text-primary leading-tight">{f.title}</div>
                    </div>
                    <div className={`h-2 w-2 rounded-full transition-colors ${isActive ? "bg-accent" : "bg-border"}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl border border-border bg-white p-8 md:p-10 shadow-sm overflow-hidden">
              <div className="absolute top-0 right-0 h-32 w-32 bg-accent/10 blur-3xl rounded-full pointer-events-none" />
              <div className="relative">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <current.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
                  {current.title}
                </h3>
                <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">
                  {current.desc}
                </p>

                <div className="mt-8 grid grid-cols-3 gap-4">
                  {current.metrics.map((m, i) => (
                    <div key={i} className="rounded-xl bg-secondary/60 border border-border p-4">
                      <div className="font-display text-2xl font-bold text-primary">{m.k}</div>
                      <div className="text-xs text-muted-foreground mt-1">{m.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
